import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { authAPI } from '../../api/auth';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // 确保username是email格式
      if (!formData.username.includes('@')) {
        setError('请使用邮箱地址登录');
        setLoading(false);
        return;
      }
      
      console.log('🔐 开始登录请求...', { email: formData.username });
      const response = await authAPI.login(formData);
      console.log('✅ 登录响应:', response);
      
      if (response && response.access_token) {
        console.log('Login: 保存token到localStorage，token长度:', response.access_token.length);
        
        // 重要：在保存新token之前，清除所有旧用户的缓存
        try {
          // 清除所有 sessionStorage 缓存
          const sessionKeysToRemove: string[] = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && (
              key.startsWith('taskManagement_cache_') ||
              key.startsWith('dashboard_cache_') ||
              key.startsWith('history_cache_') ||
              key === 'justLoggedIn'
            )) {
              sessionKeysToRemove.push(key);
            }
          }
          sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
          
          // 清除所有 localStorage 中的积分缓存
          const localKeysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('user_quota_cache')) {
              localKeysToRemove.push(key);
            }
          }
          localKeysToRemove.forEach(key => localStorage.removeItem(key));
          
          console.log(`Login: 已清除 ${sessionKeysToRemove.length} 个sessionStorage缓存和 ${localKeysToRemove.length} 个localStorage缓存`);
        } catch (e) {
          console.warn('Login: 清除缓存失败:', e);
        }
        
        // 确保token保存成功 - 尝试多次保存
        try {
          localStorage.setItem('access_token', response.access_token);
          console.log('Login: Token已写入localStorage');
          
          // 立即验证
          const savedToken = localStorage.getItem('access_token');
          console.log('Login: 立即验证 - savedToken exists:', !!savedToken, 'Length:', savedToken ? savedToken.length : 0, 'Value:', savedToken ? savedToken.substring(0, 20) + '...' : 'null');
          
          if (!savedToken || savedToken !== response.access_token) {
            console.error('Login: Token保存验证失败！重新尝试保存...');
            // 再次尝试保存
            localStorage.setItem('access_token', response.access_token);
            const retryToken = localStorage.getItem('access_token');
            console.log('Login: 重试后验证 - token exists:', !!retryToken, 'Length:', retryToken ? retryToken.length : 0);
          }
        } catch (e) {
          console.error('Login: localStorage保存失败:', e);
          setError('Token保存失败，可能是浏览器安全设置限制');
          setLoading(false);
          return;
        }
        
        // 最终验证
        const finalCheckToken = localStorage.getItem('access_token');
        console.log('Login: 最终验证 - token exists:', !!finalCheckToken);
        
        if (!finalCheckToken) {
          console.error('Login: Token最终验证失败！');
          setError('Token保存失败，请重试');
          setLoading(false);
          return;
        }
        
        // 触发storage事件，让其他组件知道登录状态已改变
        window.dispatchEvent(new Event('storage'));
        window.dispatchEvent(new Event('localStorageChange'));
        
        // 设置登录标记，让 Dashboard 强制刷新数据
        sessionStorage.setItem('justLoggedIn', 'true');
        
        // 延迟后直接跳转，使用window.location.href确保页面刷新
        setTimeout(() => {
          const jumpToken = localStorage.getItem('access_token');
          console.log('Login: 跳转前验证 - token exists:', !!jumpToken);
          
          if (jumpToken) {
            console.log('Login: Token存在，跳转到 /app');
            // 使用window.location.href强制刷新页面，ProtectedRoute会检查token
            window.location.href = '/app';
          } else {
            console.error('Login: Token在跳转前丢失！');
            setError('Token保存失败，请重试');
            setLoading(false);
          }
        }, 100); // 短暂延迟确保保存完成
      } else {
        throw new Error('登录响应格式错误');
      }
    } catch (err: any) {
      console.error('登录错误:', err);
      console.error('错误详情:', err.response?.data);
      console.error('错误状态:', err.response?.status);
      console.error('错误消息:', err.message);
      console.error('错误代码:', err.code);
      console.error('请求配置:', err.config);
      
      // 更详细的错误信息
      let errorMessage = '登录失败，请检查网络连接';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        // 请求超时
        errorMessage = '请求超时，可能是云端服务器响应慢，请稍后重试';
      } else if (err.response) {
        // 服务器返回了错误响应
        const status = err.response.status;
        const detail = err.response.data?.detail || err.response.data?.error || '';
        
        if (status === 401) {
          errorMessage = '用户名或密码错误，请检查后重试';
        } else if (status === 400) {
          errorMessage = detail || '请求格式错误，请使用邮箱地址登录';
        } else if (status === 503) {
          errorMessage = '无法连接到云端服务器（Cloudflare Worker），请检查网络或联系管理员';
        } else if (status === 504) {
          errorMessage = '云端服务器响应超时，请稍后重试';
        } else {
          errorMessage = detail || `服务器错误 (${status})`;
        }
      } else if (err.request) {
        // 请求已发送但没有收到响应
        errorMessage = '无法连接到后端服务，请检查网络连接或联系管理员';
      } else {
        // 请求配置错误
        errorMessage = err.message || '登录请求失败，请检查网络连接';
      }
      
      console.error('最终错误消息:', errorMessage);
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center">
            <img src="/2025.png" alt="Logo" className="w-16 h-16 mx-auto mb-4 object-contain" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">猫爪AI工作社</h2>
            <p className="text-gray-600">登录您的账户</p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                  邮箱地址 *
                </label>
                <input
                  id="username"
                  name="username"
                  type="email"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="请输入邮箱地址"
                />
                <p className="mt-1 text-xs text-gray-500">请使用邮箱地址登录，与PC端共享账号</p>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  密码
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="请输入密码"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '登录中...' : '登录'}
              </button>
            </div>

            <div className="text-center space-y-2">
              <Link
                to="/forgot-password"
                className="text-sm text-primary-600 hover:text-primary-500 block"
              >
                忘记密码？
              </Link>
              <Link
                to="/register"
                className="text-sm text-primary-600 hover:text-primary-500 block"
              >
                还没有账户？点击注册
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
